import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Star, Trash2 } from "lucide-react";
import {
  brokeragesApi,
  type BrokerageContactDto,
  type BrokerageDetailDto,
} from "@/api/brokerages";
import { Badge, Button, FormField, Input } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { emailFieldError } from "@/lib/emailValidator";
import { phoneFieldError } from "@/lib/phoneValidator";
import { useDialogs } from "@/hooks/useDialogs";
import { brokerageKeys } from "@/hooks/brokerages/brokerageKeys";

/**
 * Brokerage CRM parity — the firm's "Additional contacts" card, rendered on the
 * Brokerage detail Overview tab. Direct mirror of the broker `BrokerContactsCard`
 * (add / edit / remove / set-primary / activate-deactivate). The firm's own
 * free-text PrimaryContactName field is separate and untouched.
 */
export function BrokerageContactsCard({
  brokerage,
  canUpdate,
}: {
  brokerage: BrokerageDetailDto;
  canUpdate: boolean;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<{ contact: BrokerageContactDto | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dialogs = useDialogs();

  // Primary first, then active, then by name — a stable, readable order.
  // Guard against a stale React Query entry cached before this field shipped.
  const contacts = [...(brokerage.contacts ?? [])].sort((a, b) =>
    (a.isPrimary === b.isPrimary ? 0 : a.isPrimary ? -1 : 1) ||
    (a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1) ||
    `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));

  // The detail page reads via the legacy singular key ["brokerage", id]; the
  // list doesn't render contacts but cover `all` for consistency.
  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: brokerageKeys.legacyDetail(brokerage.id) }),
      qc.invalidateQueries({ queryKey: brokerageKeys.all }),
    ]);

  const setPrimary = useMutation({
    mutationFn: (c: BrokerageContactDto) =>
      brokeragesApi.upsertContact(brokerage.id, c.id, {
        firstName: c.firstName,
        lastName: c.lastName,
        title: c.title ?? null,
        email: c.email ?? null,
        phone: c.phone ?? null,
        isPrimary: true,
        isActive: true,
      }),
    onSuccess: () => void invalidate(),
    onError: (e: Error) => setError(e.message),
  });

  // Active is managed from the list (the add/edit form no longer carries the
  // toggle). Deactivating the primary contact hands primary to the next active
  // contact server-side.
  const setActive = useMutation({
    mutationFn: ({ c, active }: { c: BrokerageContactDto; active: boolean }) =>
      brokeragesApi.upsertContact(brokerage.id, c.id, {
        firstName: c.firstName,
        lastName: c.lastName,
        title: c.title ?? null,
        email: c.email ?? null,
        phone: c.phone ?? null,
        isPrimary: c.isPrimary,
        isActive: active,
      }),
    onSuccess: () => void invalidate(),
    onError: (e: Error) => setError(e.message),
  });

  const remove = async (c: BrokerageContactDto) => {
    const ok = await dialogs.confirm({
      title: "Remove contact",
      message: `Remove ${`${c.firstName} ${c.lastName}`.trim() || "this contact"}?`,
      confirmLabel: "Remove",
      destructive: true,
    });
    if (!ok) return;
    try {
      await brokeragesApi.deleteContact(brokerage.id, c.id);
      void invalidate();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Remove failed.");
    }
  };

  return (
    <div className="bg-white-pure border border-border-light rounded-lg p-space-4">
      <div className="flex items-center justify-between mb-space-2">
        <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow">
          Additional contacts
        </h3>
        {canUpdate && (
          <Button variant="secondary" onClick={() => setEditing({ contact: null })}>
            <Plus size={14} aria-hidden /> Add contact
          </Button>
        )}
      </div>
      {contacts.length === 0 ? (
        <div className="text-caption text-gray-mid italic">No contacts.</div>
      ) : (
        <ul className="divide-y divide-border-light">
          {contacts.map((c) => (
            <li key={c.id} className="flex items-start justify-between gap-space-3 py-space-3 first:pt-0">
              <div className="text-small">
                <div className="flex items-center gap-space-2">
                  <span className="font-semibold text-onyx">
                    {`${c.firstName} ${c.lastName}`.trim() || "—"}
                  </span>
                  {c.isPrimary && <Badge status="gold">Primary</Badge>}
                  {!c.isActive && <Badge status="archived">Inactive</Badge>}
                </div>
                <dl className="mt-space-1 grid grid-cols-[auto,1fr] gap-x-space-3 gap-y-space-1 text-caption text-gray-mid">
                  {c.title && (<><dt>Title</dt><dd className="text-onyx">{c.title}</dd></>)}
                  <dt>Email</dt><dd className="text-onyx">{c.email ?? "—"}</dd>
                  <dt>Phone</dt><dd className="text-onyx">{c.phone ?? "—"}</dd>
                </dl>
              </div>
              {canUpdate && (
                <div className="flex shrink-0 flex-col items-end gap-space-1">
                  <Button variant="text" size="sm" onClick={() => setEditing({ contact: c })}>
                    <Pencil size={14} aria-hidden /> Edit
                  </Button>
                  {!c.isPrimary && c.isActive && (
                    <Button
                      variant="text"
                      size="sm"
                      onClick={() => setPrimary.mutate(c)}
                      disabled={setPrimary.isPending}
                    >
                      <Star size={14} aria-hidden /> Set primary
                    </Button>
                  )}
                  {/* The primary contact is always active; to deactivate it,
                      first set another active contact as primary. */}
                  {!c.isPrimary && (
                    <Button
                      variant="text"
                      size="sm"
                      onClick={() => setActive.mutate({ c, active: !c.isActive })}
                      disabled={setActive.isPending}
                    >
                      {c.isActive ? "Deactivate" : "Activate"}
                    </Button>
                  )}
                  <Button variant="text" size="sm" onClick={() => remove(c)}>
                    <Trash2 size={14} aria-hidden /> Remove
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p className="text-caption text-status-error mt-space-2" role="alert">{error}</p>
      )}
      {editing && (
        <ContactEditModal
          brokerageId={brokerage.id}
          contact={editing.contact}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function ContactEditModal({
  brokerageId,
  contact,
  onClose,
}: {
  brokerageId: string;
  contact: BrokerageContactDto | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    firstName: contact?.firstName ?? "",
    lastName: contact?.lastName ?? "",
    title: contact?.title ?? "",
    email: contact?.email ?? "",
    phone: contact?.phone ?? "",
  });
  const [error, setError] = useState<string | null>(null);

  const emailError = emailFieldError(form.email, { required: false });
  const phoneError = phoneFieldError(form.phone, { required: false });

  const mutation = useMutation({
    mutationFn: () =>
      brokeragesApi.upsertContact(brokerageId, contact?.id, {
        firstName: form.firstName,
        lastName: form.lastName,
        title: form.title || null,
        email: form.email || null,
        phone: form.phone || null,
        // Primary is designated from the contacts list; a fresh add is active and
        // the server auto-assigns primary to the first / only active contact.
        isPrimary: contact?.isPrimary ?? false,
        isActive: contact?.isActive ?? true,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: brokerageKeys.legacyDetail(brokerageId) });
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={contact ? "Edit contact" : "Add contact"}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            loading={mutation.isPending}
            disabled={!form.firstName || !!emailError || !!phoneError}
            onClick={() => {
              setError(null);
              if (emailError || phoneError) {
                setError(emailError ?? phoneError ?? null);
                return;
              }
              mutation.mutate();
            }}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-space-3">
        <FormField label="First name" required>
          <Input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
        </FormField>
        <FormField label="Last name">
          <Input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
        </FormField>
        <FormField label="Title">
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </FormField>
        <FormField label="Email" error={emailError}>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            invalid={!!emailError}
          />
        </FormField>
        <FormField label="Phone" error={phoneError}>
          <Input
            type="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            invalid={!!phoneError}
          />
        </FormField>
      </div>
      {error && (
        <p className="text-caption text-status-error mt-space-2" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}
